'use client';

import { KeyboardArrowDownRounded } from '@mui/icons-material';
import { Box, Button, useMediaQuery, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useQuery } from '@tanstack/react-query';
import { useLocale, useTranslations } from 'next-intl';
import { useEffect, useRef, useState } from 'react';

import type { HomepageDestinationsResponse } from '@/app/api/lib/destinations/destination.types';
import { groupByZone } from '@/components/homepage/groupByZone';
import { useApiFetch } from '@/configurations/api/useApiFetch';
import { type NavTriggerContext, trackNavigationClick } from '@/configurations/tracking/navigation';

import { DestinationsMegaMenu } from './DestinationsMegaMenu';
import { DestinationsOverlay } from './DestinationsOverlay';

const CLOSE_DELAY_MS = 150;

type Props = {
    isOverlay: boolean; // = mode overlay de la navbar (fond sombre)
    triggerContext: NavTriggerContext;
};

export function DestinationsMenuButton({ isOverlay, triggerContext }: Props) {
    const t = useTranslations('nav');
    const locale = useLocale();
    const apiFetch = useApiFetch();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const [isOpen, setIsOpen] = useState(false);
    const [everOpened, setEverOpened] = useState(false);

    const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        return () => {
            if (closeTimer.current) clearTimeout(closeTimer.current);
        };
    }, []);

    const { data, isLoading, isError } = useQuery<HomepageDestinationsResponse>({
        queryKey: ['nav', 'destinations', locale],
        queryFn: async () => {
            const res = await apiFetch('/api/destinations');
            if (!res.ok) throw new Error('destinations fetch failed');
            const json = (await res.json()) as { data: HomepageDestinationsResponse };
            return json.data;
        },
        enabled: everOpened,
        staleTime: 5 * 60 * 1000,
    });

    const groups = groupByZone(data?.items ?? []);

    const open = () => {
        if (closeTimer.current) clearTimeout(closeTimer.current);
        setEverOpened(true);
        setIsOpen(true);
    };
    const scheduleClose = () => {
        closeTimer.current = setTimeout(() => setIsOpen(false), CLOSE_DELAY_MS);
    };
    const cancelClose = () => {
        if (closeTimer.current) clearTimeout(closeTimer.current);
    };
    const close = () => {
        if (closeTimer.current) clearTimeout(closeTimer.current);
        setIsOpen(false);
    };

    return (
        <Box
            onMouseEnter={isMobile ? undefined : open}
            onMouseLeave={isMobile ? undefined : scheduleClose}
        >
            <Button
                variant="text"
                onClick={() => {
                    // Sur desktop le survol a déjà ouvert le menu : le clic qui suit le referme,
                    // mais l'intention reste d'y entrer. On compte donc chaque clic.
                    trackNavigationClick({ navItem: 'destinations_menu', triggerContext });
                    if (isOpen) {
                        close();
                    } else {
                        open();
                    }
                }}
                endIcon={
                    <KeyboardArrowDownRounded
                        sx={{
                            fontSize: 16,
                            transition: 'transform 200ms ease',
                            transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                        }}
                    />
                }
                sx={(theme) => ({
                    fontWeight: 400,
                    fontSize: '1rem',
                    letterSpacing: '0.00938em',
                    lineHeight: 1.5,
                    px: 0,
                    minWidth: 0,
                    color: isOverlay
                        ? alpha(theme.palette.common.white, isOpen ? 0.95 : 0.8)
                        : isOpen
                          ? 'text.primary'
                          : 'text.secondary',
                    '&:hover': {
                        backgroundColor: 'transparent',
                        color: isOverlay ? 'common.white' : 'text.primary',
                    },
                })}
            >
                {t('destinations')}
            </Button>

            {!isMobile && (
                <DestinationsMegaMenu
                    open={isOpen}
                    groups={groups}
                    loading={isLoading}
                    isError={isError}
                    onMouseEnter={cancelClose}
                    onMouseLeave={scheduleClose}
                    onNavigate={close}
                />
            )}

            {isMobile && (
                <DestinationsOverlay
                    open={isOpen}
                    groups={groups}
                    loading={isLoading}
                    isError={isError}
                    onClose={close}
                    onNavigate={close}
                />
            )}
        </Box>
    );
}
